import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import { getAppUserId } from '../../../services/activeAppUser';
import { getInternalUserProfileById, type InternalUserProfile } from '../../../services/userProfile';
import { AUTH_STORAGE_EVENT } from '../../../utils/authStorage';
import { setFlashToast } from '../../../utils/flashToast';
import { isOtherDeliveryMethodId } from '../../../utils/deliveryMethod';
import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaWizardPath, ExtraditionMlaWizardShell, FieldError, inpClass } from './ExtraditionMlaWizardShell';
import { useExtraditionMlaLookups } from './useExtraditionMlaLookups';
import { validateExtraditionMlaStep } from './extraditionMlaStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import {
  applySubmissionFieldsToDraft,
  submissionFieldsFromInternalProfile } from './extraditionMlaSubmissionProfile';

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="ccr-fg">
      <label>{label}</label>
      <input className="ccr-inp" type="text" readOnly disabled value={value || '—'} />
    </div>
  );
}

function displayField(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

export default function ExtraditionMlaStep4SubmissionDeliveryPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const maxLen = useWizardMaxLengthChange(setErrors);
  const [profile, setProfile] = useState<InternalUserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const { deliveryMethods } = useExtraditionMlaLookups('step5');

  useEffect(() => {
    let mounted = true;
    const loadProfile = () => {
      setProfileLoading(true);
      const appUserId = getAppUserId();
      if (!appUserId) {
        setProfileLoading(false);
        setProfile(null);
        setFlashToast({ message: 'Could not load submitter details.', variant: 'error' });
        return;
      }
      void getInternalUserProfileById(appUserId).then((r) => {
        if (!mounted) return;
        setProfileLoading(false);
        if (!r.success) {
          setProfile(null);
          setFlashToast({ message: r.message ?? 'Could not load submitter details.', variant: 'error' });
          return;
        }
        setProfile(r.data);
        setDraft((d) => applySubmissionFieldsToDraft(d, submissionFieldsFromInternalProfile(r.data)));
      });
    };
    loadProfile();
    const onAuthStorage = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (key === 'appUser') loadProfile();
    };
    window.addEventListener(AUTH_STORAGE_EVENT, onAuthStorage);
    return () => {
      mounted = false;
      window.removeEventListener(AUTH_STORAGE_EVENT, onAuthStorage);
    };
  }, [setDraft]);

  const showDeliveryOther = useMemo(
    () => draft.deliveryMethodIds.some((id) => isOtherDeliveryMethodId(id, deliveryMethods)),
    [draft.deliveryMethodIds, deliveryMethods],
  );

  const clearErr = (key: string) => {
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const toggleDelivery = (id: string) => {
    setDraft((d) => {
      const has = d.deliveryMethodIds.includes(id);
      const ids = has ? d.deliveryMethodIds.filter((x) => x !== id) : [...d.deliveryMethodIds, id];
      const names = ids.map((i) => deliveryMethods.find((m) => m.id === i)?.name ?? '').filter(Boolean);
      const otherSelected = ids.some((i) => isOtherDeliveryMethodId(i, deliveryMethods));
      return {
        ...d,
        deliveryMethodIds: ids,
        deliveryMethods: names,
        deliveryOther: otherSelected ? d.deliveryOther : '' };
    });
    setErrors((e) => ({ ...e, deliveryMethodIds: '', deliveryOther: '' }));
  };

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateExtraditionMlaStep(5, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const submitterFields = profileLoading
    ? {
        division: 'Loading…',
        submittedBy: 'Loading…',
        designation: 'Loading…',
        email: 'Loading…',
        contactNumber: 'Loading…' }
    : {
        division: displayField(draft.submittedDivisionName || profile?.divisionName),
        submittedBy: displayField(draft.submittedByDisplayName || profile?.userDisplayName || profile?.username),
        designation: displayField(draft.submitterDesignation || profile?.designation),
        email: displayField(draft.submitterEmail || profile?.username),
        contactNumber: displayField(draft.submitterContactNo || profile?.contactNumber) };

  return (
    <ExtraditionMlaWizardShell currentStep={5} draft={draft}>
      <SectionCard title={f.sectionRequestSubmitterDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <ReadOnlyField label={f.submittedDivisionUnit} value={submitterFields.division} />
          <ReadOnlyField label={f.submittedBy} value={submitterFields.submittedBy} />
          <ReadOnlyField label={f.designation} value={submitterFields.designation} />
          <ReadOnlyField label={f.emailAddress} value={submitterFields.email} />
          <ReadOnlyField label={f.contactNumber} value={submitterFields.contactNumber} />
        </div>
      </SectionCard>

      <SectionCard title={f.sectionPhysicalFileDeliveryMethod}>
        <div id="emla-delivery">
          <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10 }}>
            {f.deliveryMethod} <span className="req">*</span>
          </label>
          <div className="ccr-check-grid ccr-check-grid--cols3">
            {deliveryMethods.map((m) => (
              <label key={m.id} className="ccr-check">
                <input
                  type="checkbox"
                  checked={draft.deliveryMethodIds.includes(m.id)}
                  onChange={() => toggleDelivery(m.id)}
                />
                <span>{m.name}</span>
              </label>
            ))}
          </div>
          {showDeliveryOther ? (
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>
                {f.otherDeliveryMethod}
                <span className="req"> *</span>
              </label>
              <input
                id="emla-deliveryOther"
                className={inpClass(Boolean(errors.deliveryOther))}
                type="text"
                placeholder={f.specifyDeliveryMethod}
                value={draft.deliveryOther}
                onChange={(e) => { const value = maxLen('deliveryOther', e.target.value, 255); setDraft((d) => ({ ...d, deliveryOther: value })); }}
              />
              <FieldError message={errors.deliveryOther} />
            </div>
          ) : null}
          <FieldError message={errors.deliveryMethodIds} />
        </div>
      </SectionCard>

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={5}
        onBack={() => nav(extraditionMlaWizardPath(4, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(extraditionMlaWizardPath(6, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </ExtraditionMlaWizardShell>
  );
}
